import { useEffect, useState } from 'react';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { SeatingInfo } from 'src/components/common/SeatingInfo';
import { useContent } from 'src/contexts/ContentContext';
import { WarningMessage } from 'src/core/POS/MessageWithIcon';
import { PosFormField } from 'src/core/POS/PosFormField';
import {
  getTextFieldState,
  PosTextField,
  PosTextFieldProps,
} from 'src/core/POS/PosTextField';
import { URL_REGEX } from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import { Seating, TicketUrl } from 'src/WebApiController';

import { TicketUrlForm } from '../../UploadTicketUrls';

export const ManualTicketUrlEntry = ({
  seating: { section, sectionId, rowId },
  quantity,
  readOnly = false,
}: {
  seating: Seating;
  quantity: number;
  readOnly?: boolean;
}) => {
  const {
    setValue,
    watch,
    trigger,
    formState: { isDirty },
  } = useFormContext<TicketUrlForm>();
  const [showErrors, setShowErrors] = useState<boolean>(isDirty);
  const ticketUrls = watch('ticketUrls');

  useEffect(() => {
    // Handle toggle between bulk and manual where extra ticketUrls were entered
    if (ticketUrls.length > quantity) {
      setValue('ticketUrls', ticketUrls.slice(0, quantity));
      trigger();
    } else if (isDirty) {
      // loaded from a toggle, show errors
      setShowErrors(true);
      trigger();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {ticketUrls.map((ticketTicketUrl: TicketUrl, i: number) => {
        const { row, seat } = ticketTicketUrl;
        const id = `${section}-${row}-${seat}-${i}`;

        return (
          <TicketUrlInput
            key={id}
            seating={{
              section,
              sectionId,
              row,
              rowId,
              seatFr: seat || (i + 1).toString(),
              seatTo: '',
              ticketIds: [],
              listingIds: [],
            }}
            disabled={readOnly}
            index={i}
            forceShowErrors={showErrors}
          />
        );
      })}
    </>
  );
};

const TicketUrlInput = ({
  seating,
  disabled,
  index,
}: {
  seating: Seating;
  disabled: boolean;
  index: number;
  forceShowErrors?: boolean;
}) => {
  const { register, formState } = useFormContext<TicketUrlForm>();

  const field: `ticketUrls.${number}.url` = `ticketUrls.${index}.url`;

  const error = formState.errors.ticketUrls?.[index]?.url;
  const warning = error?.type === 'warning' ? error?.message : '';
  const errorMessage = error?.type !== 'warning' ? (error?.message ?? '') : '';

  const requiredMsg = useContent(ContentId.Required);
  const invalidUrlMsg = useContent(ContentId.InvalidUrl);

  return (
    <>
      <SeatTicketUrlInput
        seating={seating}
        disabled={disabled}
        errors={errorMessage ? [errorMessage] : undefined}
        maxLength={400}
        {...register(field, {
          required: requiredMsg,
          pattern: {
            value: URL_REGEX,
            message: invalidUrlMsg,
          },
        })}
      />
      {warning && <WarningMessage message={warning} />}
    </>
  );
};

interface SeatTicketUrlInputProps extends PosTextFieldProps {
  seating: Seating;
  errors?: string[];
  disabled?: boolean;
}
export const SeatTicketUrlInput = React.forwardRef(
  (
    { seating, errors, ...props }: SeatTicketUrlInputProps,
    ref: React.Ref<HTMLInputElement>
  ) => {
    return (
      <PosFormField
        errors={props.disabled ? undefined : errors}
        label={<SeatingInfo {...seating} />}
      >
        <PosTextField
          rootProps={{
            state: getTextFieldState(errors),
          }}
          ref={ref}
          {...props}
        />
      </PosFormField>
    );
  }
);

SeatTicketUrlInput.displayName = 'SeatTicketUrlInput';
